import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Calculator, FileText, TrendingUp, History, Plus, ArrowLeft } from 'lucide-react';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/utils';
import { ValuationCalculator } from '@/components/valuation/ValuationCalculator';
import { ProfessionalReport } from '@/components/valuation/ProfessionalReport';

export function ValuationsPage() {
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'history' | 'form' | 'report'>('history');
  const [currentValuation, setCurrentValuation] = useState<any>(null);

  const fetchValuations = async () => {
    try {
      const response = await axiosInstance.get('/api/valuations');
      setValuations(response.data);
    } catch (error) {
      toast.error('Error al cargar tasaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValuations();
  }, []);

  const handleValuationSuccess = (valuation: any) => {
    setCurrentValuation(valuation);
    setView('report');
    fetchValuations(); // Refresh history
  };

  const renderContent = () => {
    switch (view) {
      case 'form':
        return (
          <div className="space-y-8 animate-in fade-in duration-500">
            <button 
              onClick={() => setView('history')}
              className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-ink/40 hover:text-terracotta transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Cancelar y Volver
            </button>
            <ValuationCalculator onSuccess={handleValuationSuccess} />
          </div>
        );
      
      case 'report':
        return (
          <div className="space-y-8 animate-in fade-in duration-500">
            <button 
              onClick={() => setView('history')}
              className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-ink/40 hover:text-terracotta transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Volver al Historial
            </button>
            <ProfessionalReport valuation={currentValuation} />
          </div>
        );

      default:
        return (
          <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-display font-black uppercase text-ink italic leading-none">Informes de Tasación</h1>
                <p className="font-serif italic text-ink/60 mt-2">Valorización inteligente de propiedades con IA.</p>
              </div>
              <Button onClick={() => setView('form')} className="h-14 px-8 gap-2 bg-ink text-parchment hover:bg-terracotta transition-all">
                <Plus className="h-5 w-5" />
                NUEVA TASACIÓN
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="brutalist-border bg-ink text-parchment p-8">
                <TrendingUp className="h-8 w-8 text-terracotta mb-4" />
                <p className="text-3xl font-display font-black">65 UF/m²</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Valor Promedio Mercado</p>
              </div>
              <div className="brutalist-border bg-parchment p-8">
                <History className="h-8 w-8 text-moss mb-4" />
                <p className="text-3xl font-display font-black">{valuations.length}</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Informes Generados</p>
              </div>
              <div className="brutalist-border bg-terracotta text-parchment p-8">
                <Calculator className="h-8 w-8 text-ink mb-4" />
                <p className="text-3xl font-display font-black">45 Días</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Tiempo Promedio Venta</p>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-terracotta mb-6">Historial de Informes</h2>
              {loading ? (
                Array(3).fill(0).map((_, i) => (
                  <div key={i} className="h-32 brutalist-border bg-ink/5 animate-pulse" />
                ))
              ) : valuations.length > 0 ? (
                valuations.map((valuation: any) => (
                  <div 
                    key={valuation._id} 
                    onClick={() => {
                      setCurrentValuation(valuation);
                      setView('report');
                    }}
                    className="brutalist-border bg-parchment p-6 flex flex-col md:flex-row justify-between items-center group hover:bg-ink hover:text-parchment transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-6 w-full md:w-auto">
                      <div className="w-12 h-12 bg-ink text-parchment group-hover:bg-terracotta flex items-center justify-center transition-colors">
                        <FileText className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-display font-black uppercase italic truncate max-w-md">{valuation.propertyData.title}</h3>
                        <p className="text-xs font-serif italic opacity-60">{valuation.propertyData.address}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-12 mt-6 md:mt-0 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-4 md:pt-0 border-ink/10 group-hover:border-parchment/10">
                      <div className="text-right">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Valor Sugerido</p>
                        <p className="text-2xl font-display font-black text-terracotta group-hover:text-parchment transition-colors">
                          {valuation.results.suggestedSalePrice} UF
                        </p>
                      </div>
                      <Button variant="outline" className="h-12 px-6 text-[10px] font-black uppercase tracking-widest group-hover:border-parchment group-hover:text-parchment">VER INFORME</Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-20 text-center brutalist-border bg-ink/5 italic text-ink/40">
                  No has generado informes de tasación aún.
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      {renderContent()}
    </DashboardLayout>
  );
}
